import classNames from 'classnames';
import { CalendarSelectField } from '~/features/shared/calendar/CalendarSelectField';
import { CalendarFilters, CalendarLayerState } from '../types';
import { FIELD_STYLES } from './controlStyles';

const FACILITY_ALL_OPTION = { value: 'all', label: 'All facilities' };
const CONTRACT_TYPE_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'JOB', label: 'Direct provider' },
  { value: 'MSAAS', label: 'B2B' },
];
const B2B_ROLE_OPTIONS = [
  { value: 'all', label: 'Buyer + Seller' },
  { value: 'BUYER', label: 'Buying capacity' },
  { value: 'SELLER', label: 'Providing capacity' },
];

const LAYER_LABELS: Record<keyof CalendarLayerState, string> = {
  contracts: 'Contracts',
  listings: 'Listings',
  b2b: 'B2B',
  billing: 'Billing',
  drafts: 'Drafts',
};

interface LayerFilterBarProps {
  layers: CalendarLayerState;
  onLayersChange: (layers: CalendarLayerState) => void;
  filters: CalendarFilters;
  onFiltersChange: (filters: CalendarFilters) => void;
  facilities: string[];
}

export const LayerFilterBar = ({
  layers,
  onLayersChange,
  filters,
  onFiltersChange,
  facilities,
}: LayerFilterBarProps) => (
  <div className="flex flex-wrap items-center gap-3">
    <div className="flex flex-wrap gap-1.5" aria-label="Event layers">
      {(Object.keys(LAYER_LABELS) as (keyof CalendarLayerState)[]).map((key) => (
        <button
          key={key}
          type="button"
          aria-pressed={layers[key]}
          onClick={() => onLayersChange({ ...layers, [key]: !layers[key] })}
          className={classNames(
            'heading-sb cursor-pointer rounded-full border px-3 py-1 transition-colors',
            'focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-primary-50/12',
            layers[key]
              ? 'border-primary-50 bg-primary-t-900 text-primary-50'
              : 'border-grey-800 bg-white text-grey-500 hover:bg-grey-900 hover:text-grey-100'
          )}
        >
          {LAYER_LABELS[key]}
        </button>
      ))}
    </div>

    <CalendarSelectField
      ariaLabel="Facility filter"
      cyTag="calendar-facility-filter"
      className="w-44"
      value={filters.facility}
      onChange={(value) => onFiltersChange({ ...filters, facility: value })}
      options={[FACILITY_ALL_OPTION, ...facilities.map((f) => ({ value: f, label: f }))]}
    />

    <CalendarSelectField
      ariaLabel="Contract type filter"
      cyTag="calendar-contract-type-filter"
      className="w-40"
      value={filters.contractType}
      onChange={(value) =>
        onFiltersChange({
          ...filters,
          contractType: value as CalendarFilters['contractType'],
        })
      }
      options={CONTRACT_TYPE_OPTIONS}
    />

    <CalendarSelectField
      ariaLabel="B2B role filter"
      cyTag="calendar-b2b-role-filter"
      className="w-48"
      value={filters.b2bRole}
      onChange={(value) =>
        onFiltersChange({ ...filters, b2bRole: value as CalendarFilters['b2bRole'] })
      }
      options={B2B_ROLE_OPTIONS}
    />

    <input
      type="search"
      aria-label="Search events"
      placeholder="Search title, counterparty, facility…"
      value={filters.search}
      onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
      className={classNames(FIELD_STYLES, 'min-w-56 flex-1 placeholder:text-grey-700')}
    />
  </div>
);
